import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { BrandLogo } from "./BrandLogo";
import { Button } from "@/components/ui/button";
import { LayoutDashboard, ShoppingBag, ClipboardList, ChefHat, Package, Wallet, Users, BarChart3, Settings, LogOut, Crown, Monitor, ScrollText, Store, Printer } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { PLANS_INFO } from "@/lib/format";
import { Toaster } from "@/components/ui/sonner";
import { TrialBanner, UpgradeGate, NotificationsBell } from "@/components/Growth";
import { Gift, UtensilsCrossed } from "lucide-react";

const NAV = [
  { to: "/app", icon: LayoutDashboard, label: "Dashboard", end: true, tid: "nav-dashboard" },
  { to: "/app/pdv", icon: ShoppingBag, label: "PDV", tid: "nav-pos" },
  { to: "/app/garcom", icon: UtensilsCrossed, label: "Modo Garçom", tid: "nav-waiter" },
  { to: "/app/pedidos", icon: ClipboardList, label: "Pedidos", tid: "nav-orders" },
  { to: "/app/cozinha", icon: ChefHat, label: "Cozinha (KDS)", tid: "nav-kitchen" },
  { to: "/app/imprimir", icon: Printer, label: "Impressão", tid: "nav-print" },
  { to: "/app/produtos", icon: ScrollText, label: "Cardápio", tid: "nav-products" },
  { to: "/app/estoque", icon: Package, label: "Estoque", tid: "nav-inventory" },
  { to: "/app/financeiro", icon: Wallet, label: "Financeiro", tid: "nav-finance" },
  { to: "/app/clientes", icon: Users, label: "Clientes", tid: "nav-crm" },
  { to: "/app/unidades", icon: Store, label: "Unidades", tid: "nav-stores" },
  { to: "/app/relatorios", icon: BarChart3, label: "Relatórios", tid: "nav-reports" },
  { to: "/app/configuracoes", icon: Settings, label: "Configurações", tid: "nav-settings" },
];

export function AppShell() {
  const { user, restaurant, logout } = useAuth();
  const nav = useNavigate();
  const plan = restaurant ? PLANS_INFO[restaurant.plan] : PLANS_INFO.free;

  return (
    <div className="min-h-screen bg-stone-50">
      <div className="flex">
        <aside className="hidden lg:flex w-64 shrink-0 flex-col border-r border-stone-200 bg-white min-h-screen sticky top-0">
          <div className="px-6 py-6 border-b border-stone-100"><BrandLogo size="md" /></div>
          <div className="px-4 py-4 border-b border-stone-100">
            <p className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Restaurante</p>
            <p className="font-display font-bold text-stone-900 truncate" data-testid="restaurant-name">{restaurant?.name}</p>
            <div className="mt-2 flex items-center gap-2">
              <Badge variant="outline" className="border-orange-200 bg-orange-50 text-orange-700" data-testid="current-plan">
                <Crown className="mr-1 h-3 w-3" /> {plan.name}
              </Badge>
              {restaurant?.subscription_status === "trial" && (
                <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-700">Trial</Badge>
              )}
            </div>
          </div>
          <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
            {NAV.map((it) => (
              <NavLink key={it.to} to={it.to} end={it.end} data-testid={it.tid}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition ${
                    isActive ? "bg-orange-50 text-orange-700" : "text-stone-600 hover:bg-stone-100 hover:text-stone-900"
                  }`
                }>
                <it.icon className="h-4 w-4" />
                {it.label}
              </NavLink>
            ))}
            <button onClick={() => nav("/app/assinatura")} data-testid="nav-subscription"
              className="w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-stone-600 hover:bg-stone-100">
              <Crown className="h-4 w-4" /> Assinatura
            </button>
            <NavLink to="/app/indicacoes" data-testid="nav-referral" className={({ isActive }) => `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition ${isActive ? "bg-orange-50 text-orange-700" : "text-stone-600 hover:bg-stone-100 hover:text-stone-900"}`}>
              <Gift className="h-4 w-4" /> Indicações
            </NavLink>
          </nav>
          <div className="p-4 border-t border-stone-100">
            <div className="text-xs text-stone-500 mb-2">{user?.email}</div>
            <Button variant="outline" size="sm" className="w-full gap-2" onClick={() => { logout(); nav("/"); }} data-testid="logout-btn">
              <LogOut className="h-4 w-4" /> Sair
            </Button>
          </div>
        </aside>
        <main className="flex-1 min-w-0">
          {/* Mobile top bar */}
          <div className="lg:hidden flex items-center justify-between px-4 py-3 border-b border-stone-200 bg-white sticky top-0 z-30">
            <BrandLogo size="sm" />
            <div className="flex items-center gap-1"><NotificationsBell /><Button size="sm" variant="ghost" onClick={() => nav("/app/assinatura")}><Crown className="h-4 w-4" /></Button></div>
          </div>
          <TrialBanner />
          <div className="hidden lg:flex justify-end px-8 pt-4"><NotificationsBell /></div>
          <div className="p-4 md:p-8 pb-24 lg:pb-8"><Outlet /></div>
          <UpgradeGate />
          {/* Mobile bottom nav */}
          <div className="lg:hidden fixed bottom-0 inset-x-0 z-30 border-t border-stone-200 bg-white grid grid-cols-5">
            {NAV.slice(0, 5).map((it) => (
              <NavLink key={it.to} to={it.to} end={it.end}
                className={({ isActive }) => `flex flex-col items-center justify-center py-2 text-xs ${isActive ? "text-orange-600" : "text-stone-500"}`}>
                <it.icon className="h-5 w-5 mb-0.5" />
                <span className="text-[10px]">{it.label.split(" ")[0]}</span>
              </NavLink>
            ))}
          </div>
        </main>
      </div>
      <Toaster position="top-right" richColors />
    </div>
  );
}
